if (!customElements.get('blog-journal')) {
  customElements.define(
    'blog-journal',
    class BlogJournal extends HTMLElement {
      static LINK_SELECTOR = '.blog-journal__tab, .blog-journal__page, .blog-journal__page-arrow';
      static CACHE_LIMIT = 20;

      constructor() {
        super();
        this.cache = new Map();
        this.requestId = 0;
        this.onClick = this.onClick.bind(this);
        this.onIntent = this.onIntent.bind(this);
        this.onPopState = this.onPopState.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.blogPath = new URL(this.dataset.blogUrl || location.pathname, location.origin).pathname;
        this.results = this.querySelector('[data-results]');
        this.tabList = this.querySelector('.blog-journal__tab-list');
        this.scrollActiveTabIntoView();
        if (!this.sectionId || !this.results || !window.fetch || !window.DOMParser) return;

        this.store(location.href, Promise.resolve(this.snapshot(this.results)));
        history.replaceState({ ...history.state, blogJournal: true }, '', location.href);

        this.addEventListener('click', this.onClick);
        this.addEventListener('pointerover', this.onIntent);
        this.addEventListener('focusin', this.onIntent);
        this.addEventListener('touchstart', this.onIntent, { passive: true });
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('pointerover', this.onIntent);
        this.removeEventListener('focusin', this.onIntent);
        this.removeEventListener('touchstart', this.onIntent);
        window.removeEventListener('popstate', this.onPopState);
        this.cache.clear();
      }

      getLink(event) {
        const link = event.target.closest && event.target.closest('a[href]');
        if (!link || !this.contains(link) || !link.matches(BlogJournal.LINK_SELECTOR)) return null;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || !url.pathname.startsWith(this.blogPath)) return null;
        return { link, url };
      }

      onClick(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = this.getLink(event);
        if (!target) return;
        event.preventDefault();
        const isPagination = !target.link.classList.contains('blog-journal__tab');
        this.navigate(target.url.href, { push: true, isPagination });
      }

      onIntent(event) {
        const connection = navigator.connection;
        if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
        const target = this.getLink(event);
        if (target) this.load(target.url.href).catch(() => {});
      }

      onPopState() {
        if (!location.pathname.startsWith(this.blogPath)) return;
        this.navigate(location.href, { push: false, isPagination: false });
      }

      key(href) {
        const url = new URL(href, location.href);
        url.searchParams.delete('sections');
        url.hash = '';
        return url.pathname + url.search;
      }

      store(href, promise) {
        const key = this.key(href);
        this.cache.delete(key);
        this.cache.set(key, promise);
        if (this.cache.size > BlogJournal.CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
        return promise;
      }

      snapshot(results) {
        return { html: results.innerHTML, title: results.dataset.title || document.title };
      }

      load(href) {
        const cached = this.cache.get(this.key(href));
        if (cached) return cached;

        const url = new URL(href, location.href);
        url.searchParams.set('sections', this.sectionId);
        const fetchSection = () =>
          fetch(url.toString(), { credentials: 'same-origin' })
            .then((response) => {
              if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
              return response.json();
            })
            .then((sections) => {
              if (typeof sections[this.sectionId] !== 'string') throw new Error('Section missing from response');
              return sections[this.sectionId];
            });
        const request = fetchSection()
          .catch(fetchSection)
          .then((html) => {
            const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-results]');
            if (!results) throw new Error('Section response has no results');
            return this.snapshot(results);
          });

        request.catch(() => this.cache.delete(this.key(href)));
        return this.store(href, request);
      }

      async navigate(href, { push, isPagination }) {
        const requestId = ++this.requestId;
        const isCached = this.cache.has(this.key(href));
        if (!isCached) this.results.setAttribute('aria-busy', 'true');

        try {
          const data = await this.load(href);
          if (requestId !== this.requestId) return;

          this.results.innerHTML = data.html;
          this.results.dataset.title = data.title;
          document.title = data.title;
          this.updateTabs(href);

          if (push) {
            const url = new URL(href, location.href);
            url.searchParams.delete('sections');
            history.pushState({ blogJournal: true }, '', url.toString());
          }

          if (isPagination) {
            this.scrollToTop();
            this.results.focus({ preventScroll: true });
          }
        } catch (error) {
          if (requestId === this.requestId) window.location.assign(href);
        } finally {
          if (requestId === this.requestId) this.results.removeAttribute('aria-busy');
        }
      }

      scrollToTop() {
        const target = this.querySelector('.blog-journal__tabs') || this.results;
        const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        if (target.getBoundingClientRect().top >= offset) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }

      updateTabs(href) {
        if (!this.tabList) return;
        const path = new URL(href, location.href).pathname.replace(/\/$/, '');
        this.tabList.querySelectorAll('.blog-journal__tab').forEach((tab) => {
          const isActive = new URL(tab.href, location.href).pathname.replace(/\/$/, '') === path;
          tab.classList.toggle('is-active', isActive);
          if (isActive) {
            tab.setAttribute('aria-current', 'page');
          } else {
            tab.removeAttribute('aria-current');
          }
        });
        this.scrollActiveTabIntoView();
      }

      scrollActiveTabIntoView() {
        if (!this.tabList || this.tabList.scrollWidth <= this.tabList.clientWidth) return;
        const active = this.tabList.querySelector('.is-active');
        if (!active) return;
        const offset = active.getBoundingClientRect().left - this.tabList.getBoundingClientRect().left;
        const padding = parseFloat(getComputedStyle(this.tabList).scrollPaddingInlineStart) || 0;
        this.tabList.scrollLeft += offset - padding;
      }
    }
  );
}
